// One menu to rule them all. A single hamburger button (top-left) opens
// a Sheet with every HackerLabs surface. Wallet pinned at top shows the
// "30M free" credit headline. Everything else is gated behind sign-in —
// unauthenticated users only see a single sign-in CTA. No iframe drawers
// or page detours: in-place panels fire window events; the rest are plain
// links. The main prompt stays the only working surface.
import { useState } from "react";
import { Link } from "react-router-dom";
import {
 Menu, Brain, Library as LibraryIcon, Bug, Trophy,
  Sparkles, Bot, Users, Wallet,
  BadgeDollarSign, Settings, LogIn, User as UserIcon, Lock,
} from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { useAuth } from "@/hooks/useAuth";
import { cn } from "@/lib/utils";

type Action =
  | { kind: "event"; event: string; label: string; Icon: any; group: string; hint?: string }
  | { kind: "link"; to: string; label: string; Icon: any; group: string; hint?: string };

const ITEMS: Action[] = [
  // Yours — in-place pops, never navigate.
  { kind: "event",  event: "open-memory",    label: "Brain",    Icon: Brain,         group: "Yours" },
  { kind: "event",  event: "open-library",   label: "Library",  Icon: LibraryIcon,   group: "Yours" },

  // Build — plain links (no iframe detours).
  { kind: "link", to: "/workspace",   label: "Workspace", Icon: Sparkles, group: "Build" },
  { kind: "link", to: "/agents",      label: "Agents",    Icon: Bot,      group: "Build" },

  // Community
  { kind: "link", to: "/squads",      label: "Squads",    Icon: Users,           group: "Community" },
  { kind: "link", to: "/leaderboard", label: "Leaders",   Icon: Trophy,          group: "Community" },

  // Account
  { kind: "link", to: "/pricing",     label: "Pricing",   Icon: BadgeDollarSign, group: "Account" },
  { kind: "link", to: "/settings",    label: "Settings",  Icon: Settings,        group: "Account" },
  { kind: "event",  event: "open-bug",  label: "Report a bug", Icon: Bug,          group: "Account" },
];

const GROUPS = ["Yours", "Build", "Community", "Account"] as const;

export default function UnifiedMenu() {
  const { user } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);

  const fire = (event: string) => {
    setMenuOpen(false);
    // Wait a tick so the menu sheet finishes closing before the next
    // sheet/popover opens — Radix doesn't love stacked overlays mid-animation.
    setTimeout(() => window.dispatchEvent(new Event(event)), 80);
  };

  return (
    <>
      {/* The single, always-visible menu trigger. Top-left, works on every
          breakpoint. */}
      <button
        type="button"
        onClick={() => setMenuOpen(true)}
        aria-label="Open menu"
        className="fixed top-3 left-3 z-40 h-10 w-10 grid place-items-center rounded-full border border-border bg-card/80 backdrop-blur hover:border-primary/50 hover:bg-card shadow-lg transition-colors"
      >
        <Menu className="w-4 h-4" />
      </button>

      <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
        <SheetContent side="left" className="w-[280px] sm:w-[320px] p-0 flex flex-col">
          <SheetHeader className="p-4 border-b border-border">
            <SheetTitle className="font-mono text-sm flex items-center gap-2">
              <Menu className="w-4 h-4 text-primary" /> Everything, one place
            </SheetTitle>
            <p className="text-[11px] text-muted-foreground font-mono">
              Sign in to unlock — the main prompt always stays put.
            </p>
          </SheetHeader>

          <div className="flex-1 overflow-y-auto p-3">
            {/* Wallet — always pinned at the top. Shows "30M free" headline so
                new users immediately see what they get without signing in. */}
            <button
              type="button"
              onClick={() => user ? fire("open-wallet") : fire("open-signup")}
              className="w-full mb-4 p-3 rounded-xl border border-primary/40 bg-primary/5 hover:bg-primary/10 hover:border-primary/60 transition-colors flex items-center gap-3 text-left"
            >
              <div className="h-9 w-9 grid place-items-center rounded-lg bg-primary/15 text-primary shrink-0">
                <Wallet className="w-4 h-4" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-mono font-semibold truncate">Wallet</div>
                <div className="text-[10px] font-mono text-muted-foreground">
                  {user ? "View balance & usage" : "Sign in to claim"}
                </div>
              </div>
              <span className="text-[10px] font-mono font-bold px-2 py-1 rounded-full bg-primary text-primary-foreground shrink-0">
                30M free
              </span>
            </button>

            {!user && (
              <div className="mb-4 p-3 rounded-xl border border-border bg-card/40">
                <p className="text-[11px] font-mono text-muted-foreground mb-2">
                  Sign in to access everything below — Brain, Library, Workspace, Agents, Squads and more.
                </p>
                <Link
                  to="/signup"
                  onClick={() => setMenuOpen(false)}
                  className="h-9 w-full flex items-center justify-center gap-2 rounded-lg bg-primary text-primary-foreground text-xs font-mono font-semibold hover:opacity-90"
                >
                  <LogIn className="w-4 h-4" /> Sign in to unlock
                </Link>
              </div>
            )}

            {GROUPS.map((group) => {
              const items = ITEMS.filter((i) => i.group === group);
              if (!items.length) return null;
              return (
                <div key={group} className={cn("mb-4", !user && "opacity-50 pointer-events-none select-none")}>
                  <div className="px-2 pb-1 text-[9px] uppercase tracking-wider font-mono text-muted-foreground/70">
                    {group}
                  </div>
                  <div className="flex flex-col gap-0.5">
                    {items.map((item) => {
                      const Icon = item.Icon;
                      const inner = (
                        <>
                          <Icon className="w-4 h-4 shrink-0" />
                          <span className="text-xs font-mono truncate flex-1 text-left">{item.label}</span>
                          {!user && <Lock className="w-3 h-3 opacity-40" />}
                        </>
                      );
                      const className = cn(
                        "h-9 px-2 flex items-center gap-2 rounded-lg w-full",
                        "text-muted-foreground hover:text-foreground hover:bg-secondary/70 transition-colors",
                      );
                      if (item.kind === "event") {
                        return (
                          <button key={item.label} type="button" className={className} onClick={() => fire(item.event)}>
                            {inner}
                          </button>
                        );
                      }
                      return (
                        <Link key={item.label} to={item.to} className={className} onClick={() => setMenuOpen(false)}>
                          {inner}
                        </Link>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Auth row pinned to bottom */}
          <div className="border-t border-border p-3">
            {user ? (
              <Link
                to="/account"
                onClick={() => setMenuOpen(false)}
                className="h-10 px-3 flex items-center gap-2 rounded-lg border border-border hover:border-primary/50 text-xs font-mono"
              >
                <UserIcon className="w-4 h-4" />
                <span className="truncate">{user.email?.split("@")[0] ?? "Account"}</span>
              </Link>
            ) : (
              <Link
                to="/signup"
                onClick={() => setMenuOpen(false)}
                className="h-10 px-3 flex items-center gap-2 rounded-lg bg-primary text-primary-foreground text-xs font-mono font-semibold hover:opacity-90"
              >
                <LogIn className="w-4 h-4" /> Sign in
              </Link>
            )}
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}